import { SectionHeader } from "@/components/common/section-components/SectionHeader";
import {
  type Option,
} from "@/components/select/AsyncAutocompleteSelect";
import { TextArea } from "@/components/text-area/TextArea";
import { AppRoutes } from "@/constants/routes";
import {
  Flex,
  Text,
} from "@chakra-ui/react";
import { useEffect, useMemo, useRef, useState } from "react";
import { useFieldArray, useForm, useWatch } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { useRoles } from "@/hooks/useRoles";
import { ProductPlanificationCultivoCard } from "./ProductPlanificationCultivoCard";
import { getUsuariosMobileByCuenta } from "@/services/users.service";
import {
  parseTonInputToNumber,
} from "@/utils/query-string";
import type { GranoProductivo } from "@/services/productive-entities.service";
import { HADifferenceModal, type HADifferenceItem } from "./HADifferenceModal";
import {
  fetchZonas,
  fetchStaticOptions,
  getAccountOptions,
  loadOptionsHandler,
  calculatePrefilledValues,
} from "../schema/aux-functions";
import { ProductPlanificationCreationForm } from "../schema/product-planification.schema";
import { createProductPlanificationHandler } from "../schema/handler-functions";
import { PPMainDataForm } from "../components/card/PPMainDataForm";

export const ProductPlanificationCreationPage = () => {
  const navigate = useNavigate();
  const { fetchPermissions } = useRoles();

  const [isActive, setIsActive] = useState(false);
  useEffect(() => setIsActive(true), []);

  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  const {
    register,
    control,
    handleSubmit,
    formState: { errors },
    setValue,
  } = useForm<ProductPlanificationCreationForm>({
    defaultValues: {
      account: null,
      idCampana: [],
      idZonaProductiva: [],
      haPropias: "",
      haAlquiladas: "",
      detalle: "",
      cultivos: [
        {
          idGrano: [],
          hectareasAsembrar: "",
          fechaEstimadaSiembra: undefined,
          consumoPropio: "",
          consumoPropioPicado: "",
          costoEstimado: "",
          toneladasRinde: "",
        },
      ],
    },
  });

  const watched = useWatch({ control });

  const watchedCultivos = useWatch({ control, name: "cultivos" });
  const watchedCampana = useWatch({ control, name: "idCampana" });

  const {
    fields: cultivoFields,
    append: appendCultivo,
    remove: removeCultivo,
  } = useFieldArray({ control, name: "cultivos" });

  const [loadingCreate, setLoadingCreate] = useState(false);
  const [loadingAccount, setLoadingAccount] = useState(false);
  const [loadingOptions, setLoadingOptions] = useState(false);

  const [cuentaUsuarios, setCuentaUsuarios] = useState<any>([]);
  const [loadingCuentaUsuarios, setLoadingCuentaUsuarios] = useState(false);

  const [campanaOptions, setCampanaOptions] = useState<
    { label: string; value: string }[]
  >([]);
  const [granoOptions, setGranoOptions] = useState<
    { label: string; value: string }[]
  >([]);
  const [granosProductivos, setGranosProductivos] = useState<GranoProductivo[]>(
    [],
  );
  const [zonaOptions, setZonaOptions] = useState<
    { label: string; value: string }[]
  >([]);
  const [loadingZonas, setLoadingZonas] = useState(false);

  const [openHADifferenceModal, setOpenHADifferenceModal] = useState(false);
  const [motivosDesvio, setMotivosDesvio] = useState<HADifferenceItem[]>([]);

  const lastDiffAbsWithMotivosRef = useRef<number | null>(null);

  const selectedZonaComercial = useMemo(() => {
    return (watched?.account as any)?.zonaComercial as string | undefined;
  }, [watched?.account]);

  const lastMbzKeyByIndexRef = useRef<Record<number, string>>({});

  const lastAccountKeyRef = useRef<string>("");

  const loadAccountOptions = (inputValue: string): Promise<Option[]> => {
    return loadOptionsHandler({
      timeoutRef,
      inputValue,
      setLoadingPersons: setLoadingAccount,
      getOptions: getAccountOptions,
    });
  };

  const handleCancel = () => {
    navigate(AppRoutes.PRODUCT_PLANIFICATION);
  };

  const getUsuariosByCuenta = async (cuenta?: string) => {
    if (!cuenta) {
      setCuentaUsuarios([]);
      return;
    }

    setLoadingCuentaUsuarios(true);
    const response = await getUsuariosMobileByCuenta({ cuenta });
    setCuentaUsuarios(response ?? []);
    setLoadingCuentaUsuarios(false);
  };

  const handleAddCultivo = () => {
    appendCultivo({
      idGrano: [],
      hectareasAsembrar: "",
      fechaEstimadaSiembra: undefined,
      consumoPropio: "",
      consumoPropioPicado: "",
      costoEstimado: "",
      toneladasRinde: "",
    });
  };

  const runFetchStaticOptions = async () => {
    await fetchStaticOptions({
      setLoadingOptions,
      setCampanaOptions,
      setGranoOptions,
      setGranosProductivos,
    });
  };

  const granosById = useMemo(() => {
    return (granosProductivos ?? []).reduce<Record<number, GranoProductivo>>(
      (acc, g) => {
        acc[g.id] = g;
        return acc;
      },
      {},
    );
  }, [granosProductivos]);

  const hasDuplicatedGranos = false;

  const granoOptionsByIndex = useMemo(() => {
    return (watchedCultivos ?? []).map(() => {
      return (granoOptions ?? []).map((o) => ({
        ...o,
        disabled: false,
      }));
    });
  }, [watchedCultivos, granoOptions]);

  const haPropiasNum = useMemo(() => {
    return parseTonInputToNumber(watched?.haPropias) ?? 0;
  }, [watched?.haPropias]);

  const haAlquiladasNum = useMemo(() => {
    return parseTonInputToNumber(watched?.haAlquiladas) ?? 0;
  }, [watched?.haAlquiladas]);

  const sumaHaCultivos = useMemo(() => {
    const cultivos = watchedCultivos ?? [];
    return cultivos.reduce((acc, c) => {
      const ha = parseTonInputToNumber(c?.hectareasAsembrar) ?? 0;
      return acc + ha;
    }, 0);
  }, [watchedCultivos]);

  const haDifference = useMemo(() => {
    return haPropiasNum + haAlquiladasNum - sumaHaCultivos;
  }, [haPropiasNum, haAlquiladasNum, sumaHaCultivos]);

  const haDifferenceAbs = useMemo(() => Math.abs(haDifference), [haDifference]);

  const motivosDesvioSum = useMemo(() => {
    return (motivosDesvio ?? []).reduce((acc, m) => acc + (m?.valorHectareas ?? 0), 0);
  }, [motivosDesvio]);

  useEffect(() => {
    if (motivosDesvioSum > 0 && haDifferenceAbs > 0) {
      lastDiffAbsWithMotivosRef.current = haDifferenceAbs;
    }
    if (motivosDesvioSum <= 0) {
      lastDiffAbsWithMotivosRef.current = null;
    }
  }, [motivosDesvioSum, haDifferenceAbs]);

  useEffect(() => {
    if (!Number.isFinite(haDifference)) return;

    if (haDifference === 0) {
      if ((motivosDesvio?.length ?? 0) > 0) setMotivosDesvio([]);
      lastDiffAbsWithMotivosRef.current = null;
      return;
    }

    const last = lastDiffAbsWithMotivosRef.current;
    if (last !== null && motivosDesvioSum > 0 && haDifferenceAbs > 0 && haDifferenceAbs !== last) {
      setMotivosDesvio([]);
      lastDiffAbsWithMotivosRef.current = null;
      return;
    }

    if (haDifferenceAbs <= 0) return;
    if (motivosDesvioSum <= 0) return;

    if (Math.abs(motivosDesvioSum - haDifferenceAbs) >= 0.00001) {
      setMotivosDesvio([]);
    }
  }, [haDifference, haDifferenceAbs, motivosDesvio, motivosDesvioSum]);

  const haDifferenceMotivosValid = useMemo(() => {
    if (!Number.isFinite(haDifference)) return true;
    if (haDifference === 0) return true;
    const sumMotivos = (motivosDesvio ?? []).reduce(
      (acc, m) => acc + (m?.valorHectareas ?? 0),
      0,
    );
    return (
      haDifferenceAbs > 0 && Math.abs(sumMotivos - haDifferenceAbs) < 0.00001
    );
  }, [haDifference, haDifferenceAbs, motivosDesvio]);

  const showHADifferenceButton = useMemo(() => {
    if (!Number.isFinite(haDifference)) return false;
    if (haDifference === 0) return false;
    const hasAnyCultivoHa = (watchedCultivos ?? []).some((c) => {
      const ha = parseTonInputToNumber((c as any)?.hectareasAsembrar) ?? 0;
      return ha > 0;
    });
    return hasAnyCultivoHa;
  }, [haDifference, watchedCultivos]);

  useEffect(() => {
    if (!isActive) return;
    void runFetchStaticOptions();
  }, [isActive]);

  useEffect(() => {
    if (!isActive) return;
    if (!selectedZonaComercial) {
      setZonaOptions([]);
      return;
    }

    setLoadingZonas(true);
    fetchZonas({ zonaComercial: selectedZonaComercial, setZonaOptions })
      .catch(() => {
        setZonaOptions([]);
      })
      .finally(() => {
        setLoadingZonas(false);
      });
  }, [isActive, selectedZonaComercial]);

  useEffect(() => {
    const accountKey = String((watched?.account as any)?.value ?? "");
    if (lastAccountKeyRef.current === accountKey) return;
    lastAccountKeyRef.current = accountKey;

    setValue("idUsuarioMobile", [] as any);
    setValue("idZonaProductiva", [] as any);
    void getUsuariosByCuenta(accountKey);

    // Trigger de llamado a MBZ para actualizar esos datos
    lastMbzKeyByIndexRef.current = {};
  }, [watched?.account, watchedCultivos, setValue]);

  // Prefill costoEstimado y toneladasRinde por cultivo
  useEffect(() => {
    const idCampana = (watchedCampana ?? [])?.[0];
    const idZonaProductiva = (watched?.idZonaProductiva ?? [])?.[0];
    if (!idCampana || !idZonaProductiva) return;

    calculatePrefilledValues({
      watchedCultivos,
      setValue,
      idCampana,
      idZonaProductiva,
      lastMbzKeyByIndexRef,
      granosById,
    });
  }, [watchedCampana, watched?.idZonaProductiva, watchedCultivos, setValue]);

  const disableAdd = useMemo(() => {
    if (loadingCreate) return true;

    const hasAccount = Boolean((watched?.account as any)?.value);
    const hasCampana = Boolean((watched?.idCampana ?? [])?.[0]);
    const hasZonaProductiva = Boolean((watched?.idZonaProductiva ?? [])?.[0]);

    const haPropiasParsed = parseTonInputToNumber(watched?.haPropias);
    const haAlquiladasParsed = parseTonInputToNumber(watched?.haAlquiladas);

    const hasHaPropias =
      haPropiasParsed !== null && haPropiasParsed !== undefined;
    const hasHaAlquiladas =
      haAlquiladasParsed !== null && haAlquiladasParsed !== undefined;

    const cultivos = watchedCultivos ?? [];
    const cultivosValid =
      cultivos.length > 0 &&
      cultivos.every((c) => {
        const idGrano = Number((c?.idGrano ?? [])?.[0] ?? 0);
        const ha = parseTonInputToNumber(c?.hectareasAsembrar) ?? 0;
        const hasFecha = Boolean(c?.fechaEstimadaSiembra);
        return Boolean(idGrano) && ha > 0 && hasFecha;
      });

    const diffAbs = haDifferenceAbs;
    const diffNeedsMotivos =
      Number.isFinite(haDifference) && haDifference !== 0;
    const sumMotivos = (motivosDesvio ?? []).reduce(
      (acc, m) => acc + (m?.valorHectareas ?? 0),
      0,
    );
    const motivosValid =
      !diffNeedsMotivos ||
      (diffAbs > 0 && Math.abs(sumMotivos - diffAbs) < 0.00001);

    return (
      !hasAccount ||
      !hasCampana ||
      !hasZonaProductiva ||
      !hasHaPropias ||
      !hasHaAlquiladas ||
      !cultivosValid ||
      !motivosValid ||
      hasDuplicatedGranos
    );
  }, [
    watched?.account,
    watched?.idCampana,
    watched?.idZonaProductiva,
    watched?.haPropias,
    watched?.haAlquiladas,
    watchedCultivos,
    loadingCreate,
    haDifference,
    haDifferenceAbs,
    motivosDesvio,
    hasDuplicatedGranos,
  ]);

  const onSubmit = async (values: ProductPlanificationCreationForm) => {
    createProductPlanificationHandler({
      setLoadingCreate,
      values,
      granosById,
      motivosDesvio,
      navigate,
      fetchPermissions,
    });
  };

  return (
    <Flex
      flexDir="column"
      paddingTop="20px"
      gap="30px"
      height="100%"
      minHeight={0}
      data-testid="product-planification-creation-page"
    >
      <SectionHeader
        title="Agregar planificación productiva"
        addButtonLabel="Agregar"
        cancelButtonLabel="Cancelar"
        handleCancel={handleCancel}
        handleAdd={handleSubmit(onSubmit)}
        disableAdd={disableAdd}
        loading={loadingCreate}
      />

      <Flex flex="1" minHeight={0} overflowY="auto" flexDir="column" gap="16px">
        <PPMainDataForm 
          errors={errors}
          control={control}
          watched={watched}
          cuentaUsuarios={cuentaUsuarios}
          loadingAccount={loadingAccount}
          loadAccountOptions={loadAccountOptions}
          loadingCreate={loadingCreate}
          loadingCuentaUsuarios={loadingCuentaUsuarios}
          loadingOptions={loadingOptions}
          campanaOptions={campanaOptions}
          zonaOptions={zonaOptions}
          loadingZonas={loadingZonas}
          showHADifferenceButton={showHADifferenceButton}
          haDifferenceMotivosValid={haDifferenceMotivosValid}
          haDifferenceAbs={haDifferenceAbs}
          motivosDesvio={motivosDesvio}
          setOpenHADifferenceModal={setOpenHADifferenceModal}
        />

        {cultivoFields.map((f, index) => (
          <ProductPlanificationCultivoCard
            key={f.id}
            index={index}
            control={control as any}
            granoOptions={(granoOptionsByIndex?.[index] ?? granoOptions) as any}
            cultivosFieldsLength={cultivoFields.length}
            loadingCreate={loadingCreate}
            removeCultivo={removeCultivo}
            handleAddCultivo={handleAddCultivo}
          />
        ))}

        <Flex flexDir="column" gap="12px">
          <Text fontWeight={700} fontSize="20px">
            Detalle
          </Text>
          <TextArea
            errors={errors as any}
            inputName="detalle"
            maxLength={50}
            register={register as any}
            placeholder="Ingresar detalle"
            helperText="Máximo 50 caracteres"
            disabled={loadingCreate}
          />
        </Flex>
      </Flex>

      <HADifferenceModal
        open={openHADifferenceModal}
        setOpen={setOpenHADifferenceModal}
        differenceHectares={haDifference}
        value={motivosDesvio}
        onChange={(next) => setMotivosDesvio(next)}
      />
    </Flex>
  );
};
